import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Building2, CheckCircle2, ExternalLink, RefreshCw, Star, Unplug, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  disconnectGoogleBusiness,
  getGoogleBusinessStatus,
  listGoogleBusinessAccounts,
  listGoogleBusinessLocations,
  selectGoogleBusinessLocation,
  startGoogleBusinessAuthorization,
} from '../../../services/api';
import {
  getGoogleBusinessCallbackErrorInfo,
  getGoogleBusinessErrorInfo,
} from '../../../utils/googleBusinessError';

const presentError = (setNotice, error, fallback) => {
  const info = getGoogleBusinessErrorInfo(error, fallback);
  setNotice(info);
  toast.error(info.message);
};

const GoogleMark = () => (
  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white shadow-sm ring-1 ring-slate-200">
    <Star size={24} className="fill-amber-400 text-amber-400" />
  </span>
);

const StepBadge = ({ number, complete = false, active = false }) => (
  <span
    aria-hidden="true"
    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-black transition-colors ${
      complete
        ? 'bg-emerald-600 text-white'
        : active
          ? 'bg-[#1a73e8] text-white'
          : 'bg-slate-200 text-slate-500'
    }`}
  >
    {complete ? <CheckCircle2 size={18} /> : number}
  </span>
);

const GoogleReviewsSettings = ({ onClose, onStatusChange }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [accountName, setAccountName] = useState('');
  const [locationName, setLocationName] = useState('');
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [showSelector, setShowSelector] = useState(false);
  const [errorNotice, setErrorNotice] = useState(null);

  const authorized = Boolean(status?.connected);
  const ready = Boolean(authorized && status?.location_selected);

  const loadAccounts = useCallback(async () => {
    const result = await listGoogleBusinessAccounts();
    const nextAccounts = Array.isArray(result?.accounts) ? result.accounts : [];
    setAccounts(nextAccounts);
    setAccountName((current) => current || status?.account_name || nextAccounts[0]?.name || '');
    return nextAccounts;
  }, [status?.account_name]);

  const loadStatus = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getGoogleBusinessStatus();
      setStatus(result);
      onStatusChange?.(result);
      setAccountName(result?.account_name || '');
      setLocationName(result?.location_name || '');
      if (result?.connected && !result?.location_selected) setShowSelector(true);
    } catch (error) {
      presentError(setErrorNotice, error, 'Não foi possível consultar a integração Google.');
    } finally {
      setLoading(false);
    }
  }, [onStatusChange]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const result = params.get('google');
    if (result === 'connected') {
      setErrorNotice(null);
      toast.success('Conta Google autorizada. Agora escolha a ficha do restaurante.');
    } else if (result === 'error') {
      const info = getGoogleBusinessCallbackErrorInfo(params.get('reason'));
      setErrorNotice(info);
      toast.error(info.message);
    }
    if (result) navigate(location.pathname, { replace: true });
    loadStatus();
  }, [loadStatus, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!showSelector || !status?.connected || accounts.length) return;
    loadAccounts().catch((error) => {
      presentError(setErrorNotice, error, 'Não foi possível listar as contas do Google.');
    });
  }, [accounts.length, loadAccounts, showSelector, status?.connected]);

  useEffect(() => {
    if (!showSelector || !accountName) {
      setLocations([]);
      return;
    }
    let active = true;
    setLoadingLocations(true);
    listGoogleBusinessLocations(accountName)
      .then((result) => {
        if (!active) return;
        const nextLocations = Array.isArray(result?.locations) ? result.locations : [];
        setLocations(nextLocations);
        setLocationName((current) => (
          nextLocations.some((item) => item.name === current) ? current : (nextLocations[0]?.name || '')
        ));
      })
      .catch((error) => {
        if (active) presentError(setErrorNotice, error, 'Não foi possível listar as fichas do Google.');
      })
      .finally(() => {
        if (active) setLoadingLocations(false);
      });
    return () => { active = false; };
  }, [accountName, showSelector]);

  const selectedLocation = useMemo(
    () => locations.find((item) => item.name === locationName),
    [locationName, locations],
  );

  const handleConnect = async () => {
    try {
      setWorking(true);
      setErrorNotice(null);
      const result = await startGoogleBusinessAuthorization();
      if (!result?.authorization_url) throw new Error('URL de autorização não retornada pelo servidor.');
      window.location.assign(result.authorization_url);
    } catch (error) {
      setWorking(false);
      presentError(setErrorNotice, error, 'Não foi possível iniciar a autorização Google.');
    }
  };

  const handleSaveLocation = async () => {
    if (!accountName || !locationName) return;
    try {
      setWorking(true);
      setErrorNotice(null);
      const result = await selectGoogleBusinessLocation(accountName, locationName);
      setStatus(result);
      onStatusChange?.(result);
      setShowSelector(false);
      toast.success('Ficha do Google conectada ao FeedbackHero.');
    } catch (error) {
      presentError(setErrorNotice, error, 'Não foi possível salvar a ficha selecionada.');
    } finally {
      setWorking(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Desconectar a conta Google deste restaurante?')) return;
    try {
      setWorking(true);
      setErrorNotice(null);
      await disconnectGoogleBusiness();
      const disconnectedStatus = { status: 'disconnected', connected: false, location_selected: false };
      setStatus(disconnectedStatus);
      onStatusChange?.(disconnectedStatus);
      setAccounts([]);
      setLocations([]);
      setAccountName('');
      setLocationName('');
      setShowSelector(false);
      toast.success('Conta Google desconectada.');
    } catch (error) {
      presentError(setErrorNotice, error, 'Não foi possível desconectar a conta Google.');
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="w-full">
      <section className="overflow-hidden bg-white">
        <div className="border-b border-slate-100 bg-gradient-to-r from-blue-50 via-white to-emerald-50 p-6 sm:p-8">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
            <GoogleMark />
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl font-bold tracking-tight text-slate-900">Conectar avaliações do Google</h1>
              <p className="mt-1 text-sm leading-relaxed text-slate-600">
                Autorize o Google Business Profile e escolha a ficha que será exibida no FeedbackHero.
              </p>
            </div>
            <div className="flex items-center gap-2 self-start sm:self-center">
              <span className={`w-fit rounded-full px-3 py-1 text-xs font-bold ${
                ready ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'
              }`}>
                {ready ? 'Conectado' : status?.connected ? 'Seleção pendente' : 'Desconectado'}
              </span>
              {onClose && (
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Fechar configuração do Google"
                  className="rounded-xl p-2 text-slate-500 hover:bg-white hover:text-slate-800"
                >
                  <X size={20} />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-6 p-6 sm:p-8">
          {errorNotice && (
            <div role="alert" className="flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-rose-950">
              <AlertTriangle size={21} className="mt-0.5 shrink-0 text-rose-600" />
              <div className="min-w-0 flex-1">
                <p className="font-semibold">Não foi possível concluir esta ação</p>
                <p className="mt-1 text-sm leading-relaxed">{errorNotice.message}</p>
                {errorNotice.action && <p className="mt-1 text-sm font-medium">{errorNotice.action}</p>}
              </div>
              <button type="button" onClick={() => setErrorNotice(null)} aria-label="Fechar aviso" className="rounded-lg p-1 text-rose-600 hover:bg-rose-100">
                <X size={17} />
              </button>
            </div>
          )}
          {loading ? (
            <div className="flex items-center gap-3 rounded-xl bg-slate-50 p-4 text-sm text-slate-600">
              <RefreshCw size={18} className="animate-spin" /> Consultando integração…
            </div>
          ) : (
            <div className="space-y-4">
              <section className={`rounded-2xl border p-5 transition-colors ${
                authorized ? 'border-emerald-200 bg-emerald-50/55' : 'border-blue-200 bg-blue-50/55'
              }`}>
                <div className="flex items-start gap-3">
                  <StepBadge number="1" complete={authorized} active={!authorized} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="font-bold text-slate-900">Autorize sua conta Google</h2>
                      {authorized && (
                        <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-bold text-emerald-800">
                          Etapa concluída
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm leading-relaxed text-slate-600">
                      {authorized
                        ? 'A conta foi autorizada. Agora continue na etapa 2 para escolher a ficha do restaurante.'
                        : 'Entre com uma conta que seja proprietária ou administradora da ficha do restaurante.'}
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={handleConnect}
                        disabled={working}
                        className={`${authorized ? 'border border-blue-200 bg-white text-blue-700 hover:bg-blue-50' : 'bg-[#1a73e8] text-white shadow-sm hover:bg-[#155fc0]'} inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold disabled:opacity-60`}
                      >
                        <ExternalLink size={17} />
                        {working ? 'Abrindo Google…' : authorized ? 'Autorizar outra conta' : 'Autorizar com Google'}
                      </button>
                      <a
                        href="https://business.google.com/"
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold text-slate-600 hover:bg-white"
                      >
                        Abrir Google Business Profile <ExternalLink size={15} />
                      </a>
                    </div>
                  </div>
                </div>
              </section>

              <section className={`rounded-2xl border p-5 transition-colors ${
                ready && !showSelector
                  ? 'border-emerald-200 bg-emerald-50/55'
                  : authorized
                    ? 'border-blue-200 bg-blue-50/35'
                    : 'border-slate-200 bg-slate-50/70'
              }`}>
                <div className="flex items-start gap-3">
                  <StepBadge number="2" complete={ready && !showSelector} active={authorized && (!ready || showSelector)} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className={`font-bold ${authorized ? 'text-slate-900' : 'text-slate-500'}`}>
                        Escolha a ficha do restaurante
                      </h2>
                      {ready && !showSelector && (
                        <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-bold text-emerald-800">
                          Etapa concluída
                        </span>
                      )}
                    </div>

                    {!authorized ? (
                      <p className="mt-1 text-sm text-slate-500">
                        Esta etapa será liberada assim que a conta Google for autorizada.
                      </p>
                    ) : ready && !showSelector ? (
                      <div className="mt-3 flex flex-col gap-3 rounded-xl border border-emerald-200 bg-white/80 p-4 sm:flex-row sm:items-center">
                        <Building2 size={22} className="shrink-0 text-emerald-700" />
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-slate-900">{status.location_display_name}</p>
                          <p className="mt-0.5 text-xs text-slate-600">{status.account_display_name || status.account_name}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowSelector(true)}
                          className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                        >
                          Trocar ficha
                        </button>
                      </div>
                    ) : (
                      <div className="mt-4 space-y-4">
                        <p className="text-sm text-slate-600">
                          Selecione primeiro a conta do Business Profile e depois a ficha correspondente a este restaurante.
                        </p>
                        <label className="block text-sm font-semibold text-slate-700">
                          Conta do Business Profile
                          <select
                            value={accountName}
                            onChange={(event) => { setAccountName(event.target.value); setLocationName(''); }}
                            className="mt-1.5 h-11 w-full rounded-xl border border-slate-300 bg-white px-3 font-normal outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                          >
                            {!accounts.length && <option value="">Nenhuma conta encontrada</option>}
                            {accounts.map((account) => (
                              <option key={account.name} value={account.name}>{account.display_name}</option>
                            ))}
                          </select>
                        </label>
                        <label className="block text-sm font-semibold text-slate-700">
                          Ficha do restaurante
                          <select
                            value={locationName}
                            onChange={(event) => setLocationName(event.target.value)}
                            disabled={loadingLocations || !accountName}
                            className="mt-1.5 h-11 w-full rounded-xl border border-slate-300 bg-white px-3 font-normal outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100"
                          >
                            {!locations.length && (
                              <option value="">{loadingLocations ? 'Carregando fichas…' : 'Nenhuma ficha encontrada'}</option>
                            )}
                            {locations.map((item) => (
                              <option key={item.name} value={item.name}>{item.display_name}</option>
                            ))}
                          </select>
                        </label>
                        {selectedLocation?.store_code && (
                          <p className="text-xs text-slate-500">Código da loja: {selectedLocation.store_code}</p>
                        )}
                        <div className="flex flex-wrap justify-end gap-2">
                          {ready && (
                            <button type="button" onClick={() => setShowSelector(false)} className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-blue-100">
                              Cancelar
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={handleSaveLocation}
                            disabled={working || loadingLocations || !accountName || !locationName}
                            className="rounded-xl bg-[#1a73e8] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#155fc0] disabled:opacity-50"
                          >
                            {working ? 'Salvando ficha…' : 'Confirmar esta ficha'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </section>

              <section className={`rounded-2xl border p-5 transition-colors ${
                ready ? 'border-emerald-200 bg-emerald-50/55' : 'border-slate-200 bg-slate-50/70'
              }`}>
                <div className="flex items-start gap-3">
                  <StepBadge number="3" complete={ready} active={ready} />
                  <div className="min-w-0 flex-1">
                    <h2 className={`font-bold ${ready ? 'text-slate-900' : 'text-slate-500'}`}>Consulte suas avaliações</h2>
                    <p className="mt-1 text-sm text-slate-600">
                      {ready
                        ? 'Tudo pronto. As avaliações da ficha selecionada ficam disponíveis no painel do FeedbackHero.'
                        : 'Depois de confirmar a ficha, suas avaliações ficarão disponíveis no painel do FeedbackHero.'}
                    </p>
                    {ready && (
                      <button
                        type="button"
                        onClick={() => {
                          onClose?.();
                          navigate('/feedback-hero');
                        }}
                        className="mt-4 inline-flex items-center gap-2 rounded-xl bg-emerald-700 px-4 py-2.5 text-sm font-bold text-white hover:bg-emerald-800"
                      >
                        <Star size={17} /> Ver avaliações no painel
                      </button>
                    )}
                  </div>
                </div>
              </section>

              {authorized && (
                <div className="flex justify-end border-t border-slate-100 pt-4">
                  <button type="button" onClick={handleDisconnect} disabled={working} className="inline-flex items-center gap-2 rounded-xl border border-rose-200 px-4 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50">
                    <Unplug size={16} /> Desconectar conta Google
                  </button>
                </div>
              )}
            </div>
          )}

          <p className="text-xs leading-relaxed text-slate-500">
            As avaliações são consultadas diretamente no Google quando você abre a tela. O FeedbackHero não cria uma cópia permanente desse conteúdo.
          </p>
        </div>
      </section>
    </div>
  );
};

export default GoogleReviewsSettings;
